import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";
import { Timestamp } from "firebase/firestore";
import { Pagination } from "@/components/ui/Pagination";
import { DateRangeFilter } from "@/components/ui/DateRangeFilter";
import { Input } from "@/components/ui/Input";

interface Transcript {
  id: string;
  companyId: string;
  companyName?: string;
  ticker?: string;
  quarter: string;
  year: number;
  date: any;
}

interface Company {
  id: string;
  name: string;
  ticker: string;
}

export const EarningsCalls: React.FC = () => {
  const navigate = useNavigate();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTranscripts();
  }, [currentPage, itemsPerPage, startDate, endDate, selectedTicker, searchQuery]);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      setError(null);
      const constraints: any[] = [];

      if (selectedTicker) {
        const ticker = selectedTicker.toUpperCase();
        constraints.push(
          firestoreService.where("companyId", "==", ticker)
        );
      }

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        constraints.push(
          firestoreService.where("date", ">=", Timestamp.fromDate(start))
        );
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        constraints.push(
          firestoreService.where("date", "<=", Timestamp.fromDate(end))
        );
      }

      constraints.push(firestoreService.orderBy("date", "desc"));
      constraints.push(firestoreService.limit(itemsPerPage * currentPage));

      const allTranscripts = await firestoreService.getDocuments<Transcript>(
        "transcripts",
        constraints
      );

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedTranscripts = allTranscripts.slice(startIndex, endIndex);

      const transcriptsWithCompany = await Promise.all(
        paginatedTranscripts.map(async (transcript) => {
          const cid = (transcript.companyId || "").toString();
          try {
            const company = await firestoreService.getDocument<Company>(
              "companies",
              cid
            );
            return {
              ...transcript,
              companyName: company?.name || "Unknown",
              ticker: company?.ticker || cid || "",
            };
          } catch {
            return {
              ...transcript,
              companyName: "Unknown",
              ticker: cid || "",
            };
          }
        })
      );

      const filtered = searchQuery
        ? transcriptsWithCompany.filter(
            (t) =>
              t.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              t.ticker?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : transcriptsWithCompany;

      setTranscripts(filtered);
      setTotalPages(Math.ceil(allTranscripts.length / itemsPerPage));
    } catch (err) {
      console.error("Failed to load transcripts:", err);
      setError(err instanceof Error ? err.message : "Failed to load transcripts.");
      setTranscripts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedTicker("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Earnings Calls</h1>
          <p className="body-text text-gray-600">
            Browse all earnings call transcripts across all companies
          </p>
        </div>

        <Card className="mb-6 p-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Search Company
                </label>
                <Input
                  type="text"
                  placeholder="Company name or ticker"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="min-w-[200px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Filter by Ticker
                </label>
                <Input
                  type="text"
                  placeholder="e.g., AAPL"
                  value={selectedTicker}
                  onChange={(e) => {
                    setSelectedTicker(e.target.value.toUpperCase());
                    setCurrentPage(1);
                  }}
                />
              </div>
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(date) => {
                  setStartDate(date);
                  setCurrentPage(1);
                }}
                onEndDateChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
              />
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All
              </Button>
            </div>
          </div>
        </Card>

        {error ? (
          <Card className="p-6">
            <p className="body-text text-red-600 mb-2">{error}</p>
            <Button variant="outline" onClick={() => { setError(null); loadTranscripts(); }}>Try again</Button>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
          </div>
        ) : transcripts.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No earnings calls found. Try adjusting your filters.
              </p>
            </div>
          </Card>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {transcripts.map((transcript) => (
                <Card
                  key={transcript.id}
                  hover
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`/companies/${(transcript.ticker || transcript.companyId || "").toUpperCase()}?transcript=${transcript.id}`)
                  }
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="body-text font-mono text-blazerex-green font-medium">
                          {transcript.ticker}
                        </span>
                        <span className="sub-header text-xl">
                          {transcript.companyName}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>
                          Q{transcript.quarter} {transcript.year}
                        </span>
                        {transcript.date && (
                          <span>
                            {new Date(
                              transcript.date.toDate?.() || transcript.date
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View Analysis →
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(items) => {
                  setItemsPerPage(items);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

